import { AxiosError } from "axios";

import { useMutation, useQuery } from "@tanstack/react-query";
import { useAuth } from "../useAuth";
import { z } from "zod";
import axiosInstance from "@/lib/axios";

export type SettingsResponseType = {
  currency?: string;
  enable_nvr?: boolean;
  is_24_clock_type?: boolean;
  max_nvd_lookback?: number;
  number_format?: string;
  time_format?: string;
};

const FetchSettingsSchema = z.object({});

export type FetchSettingsProps = z.infer<typeof FetchSettingsSchema>;

async function fetchSettings(): Promise<SettingsResponseType | null> {
  try {
    const response = await axiosInstance.get(
      `${process.env.NEXT_PUBLIC_BACKEND_BASE_URL}/account/settings/get`
    );
    if (response.status !== 200) {
      throw new Error(response.data.error);
    }
    if (response.data.error) {
      throw new Error(response.data.error);
    }

    const result = response.data.result;
    return result;
  } catch (err: any) {
    if (
      err &&
      typeof err === "object" &&
      "isAxiosError" in err &&
      err.isAxiosError
    ) {
      if (!err.response) {
        throw err;
      }
      const axiosError = err as AxiosError;
      const errorMessage = axiosError.response?.data as {
        error: string;
        success: boolean;
      };
      throw new Error(errorMessage?.error ?? axiosError?.message);
    }
    if (err.message === "Token is invalid. Please sign in again.") {
      throw err;
    }
    throw new Error("Unknown error occurred! Please refresh the page.");
  }
}

function useSettings() {
  const { data } = useAuth();
  const settingsQuery = useQuery({
    queryKey: ["settings"],
    queryFn: () => fetchSettings(),
    refetchOnWindowFocus: false,
    staleTime: 30000,
    gcTime: 30000,
    enabled: !!data?.token,
  });

  async function updateSettings(newSettings: SettingsResponseType) {
    try {
      return await axiosInstance.post(
        `${process.env.NEXT_PUBLIC_BACKEND_BASE_URL}/account/settings/update`,
        newSettings
      );
    } catch (err: any) {
      if (
        err &&
        typeof err === "object" &&
        "isAxiosError" in err &&
        err.isAxiosError
      ) {
        if (!err.response) {
          throw err;
        }
        const axiosError = err as AxiosError;
        const errorMessage = axiosError.response?.data as {
          error: string;
          success: boolean;
        };
        // throw new Error(errorMessage?.error ?? axiosError?.message);
      }
      //   throw new Error("Unknown error occurred while updating settings.");
    }
  }

  return {
    settingsQuery,
    updateSettings,
  };
}

export { useSettings };
